import { Box } from '@mui/material';
import { canSetEmployeeInactive } from 'api/requests/employee-requests';
import UPCheckbox from 'components/app-container/checkbox-with-label';
import UPDatepicker from 'components/datepicker';
import { CompanyRole } from 'constants/CompanyRole';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useAppSelector } from 'store';
import { employeeSelector } from 'store/selectors/employee-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  EMPLOYEES__CANNOT_SWITCH_TERMINATED,
  EMPLOYEES__ELIGIBLE,
  EMPLOYEES__EMPLOYMENT,
  EMPLOYEES__TERMINATED,
  EMPLOYEES__TERMINATION_DATE,
} from 'translations/constants';
import { getInputErrorText } from 'utils/helpers';
import {
  ErrorText,
  SubsectionDateContainer,
  SubsectionErrorContainer,
  SubsectionInputContainer,
  SubsectionTitle,
  SubsectionTitleContainer,
} from './styles';
import { EmployeeTerminationProps } from './types';

const EligibilityAndTerminationDetails = (props: EmployeeTerminationProps) => {
  const employee = useAppSelector(employeeSelector);

  const { companyId, control, errors, watch, getValues } = props;
  const [canBeInactive, setCanBeInactive] = useState<boolean>(false);
  const [terminationCheckCompleted, setTerminationCheckCompleted] = useState<boolean>(false);
  const watchTerminated = watch('terminated');
  const watchTerminatedDate = watch('terminatedDate');

  useEffect(() => {
    const canTerminateEmployee = async () => {
      const terminationDate = getValues('terminatedDate');
      const canInactive = await canSetEmployeeInactive(employee.id, +companyId, terminationDate);
      setCanBeInactive(canInactive);
      setTerminationCheckCompleted(true);
    };

    if (watchTerminated && watchTerminatedDate) {
      setTerminationCheckCompleted(false);
      canTerminateEmployee();
    } else {
      setCanBeInactive(false);
      setTerminationCheckCompleted(false);
    }
  }, [watchTerminated, watchTerminatedDate, employee, companyId, getValues]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <SubsectionTitleContainer>
        <SubsectionTitle>
          <strong>{t(EMPLOYEES__EMPLOYMENT)}</strong>
        </SubsectionTitle>
      </SubsectionTitleContainer>
      <SubsectionInputContainer>
        <Box sx={{ paddingBottom: '5px' }}>
          <Controller
            name="eligible"
            control={control}
            render={({ field }) => (
              <UPCheckbox label={t(EMPLOYEES__ELIGIBLE)} value={field.value} handleChange={field.onChange} />
            )}
          />
          <Controller
            name="terminated"
            control={control}
            render={({ field }) => (
              <UPCheckbox label={t(EMPLOYEES__TERMINATED)} value={field.value} handleChange={field.onChange} />
            )}
          />
        </Box>
        <SubsectionDateContainer>
          {watchTerminated && (
            <Controller
              name="terminatedDate"
              control={control}
              rules={{ required: watchTerminated }}
              render={({ field }) => (
                <UPDatepicker
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                  }}
                  label={t(EMPLOYEES__TERMINATION_DATE)}
                  required={watchTerminated}
                  error={!!errors.terminatedDate}
                  helperText={getInputErrorText(errors, 'terminatedDate', undefined, 50)}
                  className="datepicker--white-background"
                />
              )}
            />
          )}
        </SubsectionDateContainer>
      </SubsectionInputContainer>
      {watchTerminated && watchTerminatedDate && terminationCheckCompleted && !canBeInactive && (
        <SubsectionErrorContainer>
          <ErrorText>{t(EMPLOYEES__CANNOT_SWITCH_TERMINATED)}</ErrorText>
        </SubsectionErrorContainer>
      )}
    </Box>
  );
};

export default EligibilityAndTerminationDetails;
